import { useEffect, useRef, useState } from "react";
import { SITE_MEDIA } from "@/constants/siteMedia";
import { openLiveDemoChat } from "@/constants/homeSections";

function HeroShaderBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const t = (ts - startRef.current) / 1000;
      const w = canvas.width;
      const h = canvas.height;

      // Base gradient
      const base = ctx.createLinearGradient(0, 0, w, h);
      base.addColorStop(0, "#ede8ff");
      base.addColorStop(0.5, "#f0eeff");
      base.addColorStop(1, "#f8f5ff");
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      // Animated orbs
      const orbs = [
        { x: 0.15 + 0.08 * Math.sin(t * 0.4), y: 0.25 + 0.06 * Math.cos(t * 0.3), r: 0.38, c1: "rgba(168,85,247,0.28)", c2: "rgba(168,85,247,0)" },
        { x: 0.75 + 0.07 * Math.cos(t * 0.35), y: 0.55 + 0.08 * Math.sin(t * 0.45), r: 0.32, c1: "rgba(124,58,237,0.22)", c2: "rgba(124,58,237,0)" },
        { x: 0.5 + 0.1 * Math.sin(t * 0.25), y: 0.1 + 0.05 * Math.cos(t * 0.5), r: 0.28, c1: "rgba(196,181,253,0.35)", c2: "rgba(196,181,253,0)" },
        { x: 0.85 + 0.05 * Math.sin(t * 0.6), y: 0.15 + 0.07 * Math.cos(t * 0.4), r: 0.22, c1: "rgba(244,114,182,0.15)", c2: "rgba(244,114,182,0)" },
        { x: 0.1 + 0.06 * Math.cos(t * 0.55), y: 0.8 + 0.05 * Math.sin(t * 0.35), r: 0.25, c1: "rgba(139,92,246,0.2)", c2: "rgba(139,92,246,0)" },
      ];

      orbs.forEach((orb) => {
        const cx = orb.x * w;
        const cy = orb.y * h;
        const radius = orb.r * Math.max(w, h);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        g.addColorStop(0, orb.c1);
        g.addColorStop(1, orb.c2);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });

      // Subtle wave lines
      ctx.save();
      ctx.globalAlpha = 0.07;
      ctx.strokeStyle = "#7C3AED";
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        const yBase = h * (0.3 + i * 0.2);
        for (let x = 0; x <= w; x += 4) {
          const y = yBase + Math.sin((x / w) * Math.PI * 3 + t * 0.6 + i * 1.2) * 30;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.restore();

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}

const chatMessages = [
  { from: "user", text: "I feel like nobody really checks on me anymore." },
  { from: "ava", text: "That's a heavy feeling to carry. It can hurt when it feels like you're always the one reaching out… I'm really glad you said it here." },
  { from: "user", text: "I don't even know why I feel like this." },
  { from: "ava", text: "You don't need to have all the answers right now. Sometimes just being heard is enough to start." },
];

/* Previous subheadline copy kept for reference:
   "Ava is your private AI companion—designed to listen,"
   "Understand, and support you whenever you need to talk."
   "No judgment. No pressure. Just real conversation."
*/
const subheadlineLines = [
  "Ava is your private AI companion—she listens,",
  "understands, and stays with you whenever you need it.",
  "No judgment. No pressure. Just someone who stays.",
];

function TypewriterSubheadline() {
  const [lineIndex, setLineIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [phase, setPhase] = useState<"typing" | "pausing" | "deleting">("typing");
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    const currentLine = subheadlineLines[lineIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === "typing") {
      if (charIndex < currentLine.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentLine.slice(0, charIndex + 1));
          setCharIndex((c) => c + 1);
        }, 38);
      } else {
        // Done typing this line — pause
        timeout = setTimeout(() => {
          setPhase("deleting");
        }, 2800);
      }
    } else if (phase === "deleting") {
      if (charIndex > 0) {
        timeout = setTimeout(() => {
          setDisplayText(currentLine.slice(0, charIndex - 1));
          setCharIndex((c) => c - 1);
        }, 18);
      } else {
        // Done deleting — switch to next line
        setDisplayText("");
        setLineIndex((i) => (i + 1) % subheadlineLines.length);
        setPhase("typing");
      }
    }

    return () => clearTimeout(timeout);
  }, [charIndex, phase, lineIndex]);

  return (
    <span
      className="text-transparent bg-clip-text"
      style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
    >
      {displayText}
      <span className="animate-pulse text-[#7C3AED]">|</span>
    </span>
  );
}

function PhoneMockup() {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [typing, setTyping] = useState(false);
  const [revealedIds, setRevealedIds] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom whenever messages or typing indicator changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [visibleMessages, typing]);

  // Trigger fade-in for each newly revealed message
  useEffect(() => {
    if (visibleMessages > 0) {
      const newId = visibleMessages - 1;
      // Small delay so the element is in the DOM before we add the class
      const t = setTimeout(() => {
        setRevealedIds((prev) => new Set(prev).add(newId));
      }, 30);
      return () => clearTimeout(t);
    }
  }, [visibleMessages]);

  useEffect(() => {
    if (visibleMessages >= chatMessages.length) {
      const t = setTimeout(() => {
        setVisibleMessages(0);
        setRevealedIds(new Set());
      }, 3000);
      return () => clearTimeout(t);
    }
    const msg = chatMessages[visibleMessages];
    if (msg.from === "ava") {
      setTyping(true);
      const t = setTimeout(() => {
        setTyping(false);
        setVisibleMessages((v) => v + 1);
      }, 1600);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setVisibleMessages((v) => v + 1), 1000);
      return () => clearTimeout(t);
    }
  }, [visibleMessages]);

  return (
    <div className="relative w-52 md:w-60">
      <style>{`
        @keyframes msgFadeSlide {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .msg-enter {
          animation: msgFadeSlide 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .msg-hidden {
          opacity: 0;
        }
      `}</style>
      <div
        className="absolute inset-0 rounded-[36px] blur-2xl opacity-20 animate-pulse"
        style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)" }}
      />
      <div
        className="relative rounded-[36px] overflow-hidden border border-[#d4c8f5]"
        style={{ background: "#eeeaff", height: "560px" }}
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-xl border border-[#d4c8f5] bg-white cursor-pointer">
            <i className="ri-arrow-left-line text-[#2d1b69] text-xs"></i>
          </div>
          <span className="font-bold text-[#2d1b69] text-xs tracking-wide">AVA CHAT AI</span>
          <div className="w-7 h-7 flex items-center justify-center rounded-full border border-[#d4c8f5] bg-white cursor-pointer">
            <i className="ri-vip-diamond-line text-[#7C3AED] text-xs"></i>
          </div>
        </div>
        <div className="flex flex-col items-center pt-2 pb-1">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center border-2 border-[#d4c8f5] overflow-hidden">
            <img
              src={SITE_MEDIA.avaIllustration}
              alt="Ava Bot"
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-[#7C3AED] text-xs font-semibold mt-1">Ava</p>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
            <span className="text-[10px] text-[#7a6aaa]">Online</span>
          </div>
        </div>
        {/* Scrollable message area — auto-scrolls on new messages */}
        <div
          ref={scrollRef}
          className="px-3 py-2 flex flex-col gap-2.5 overflow-y-auto"
          style={{ height: "280px", scrollBehavior: "smooth" }}
        >
          {chatMessages.slice(0, visibleMessages).map((msg, i) => (
            <div
              key={i}
              className={`flex ${
                msg.from === "user" ? "justify-end" : "justify-start items-end gap-1.5"
              } ${revealedIds.has(i) ? "msg-enter" : "msg-hidden"}`}
            >
              {msg.from === "ava" && (
                <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={SITE_MEDIA.avaIllustration}
                    alt="Ava"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div
                className={`rounded-2xl px-2.5 py-1.5 max-w-[150px] text-xs leading-relaxed ${
                  msg.from === "user"
                    ? "text-white rounded-br-sm"
                    : "bg-white text-[#1a1040] rounded-bl-sm"
                }`}
                style={
                  msg.from === "user"
                    ? { background: "linear-gradient(135deg, #7C3AED, #A855F7)" }
                    : {}
                }
              >
                {msg.text}
              </div>
              {msg.from === "user" && (
                <div className="w-6 h-6 rounded-full bg-[#f0c0b0] flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-[#8b4513]" style={{ fontSize: "10px" }}></i>
                </div>
              )}
            </div>
          ))}
          {typing && (
            <div className="flex items-end gap-1.5 msg-enter">
              <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={SITE_MEDIA.avaIllustration}
                  alt="Ava"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-white rounded-2xl rounded-bl-sm px-3 py-2 flex gap-1 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
            </div>
          )}
        </div>
        <div className="px-3 pb-5 pt-1">
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              className="px-3 py-1 rounded-full text-xs font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: "#7C3AED" }}
            >
              Text
            </button>
            <button type="button" className="px-3 py-1 rounded-full text-xs font-medium text-[#7a6aaa] cursor-pointer whitespace-nowrap">
              Voice
            </button>
            <button type="button" className="px-3 py-1 rounded-full text-xs font-medium text-[#7a6aaa] cursor-pointer whitespace-nowrap">
              Video
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-2xl px-3 py-2 border border-[#d4c8f5]">
            <span className="text-[#aaa] text-xs flex-1">What&apos;s on your mind?</span>
            <div
              className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
              style={{ background: "#7C3AED" }}
            >
              <i className="ri-mic-line text-white text-xs"></i>
            </div>
          </div>
          <p className="text-[#7a6aaa] text-xs mt-1.5 text-center">Enjoy 10 free chats today</p>
        </div>
      </div>
    </div>
  );
}

const orbitLabels = [
  { text: "Private & Safe", icon: "ri-shield-check-line", color: "#10b981", bg: "#f0fdf4", border: "#10b981", angle: 330, radius: 240 },
  { text: "Remembers You", icon: "ri-brain-line", color: "#f59e0b", bg: "#fffbeb", border: "#f59e0b", angle: 30, radius: 240 },
  { text: "24/7 Available", icon: "ri-time-line", color: "#7C3AED", bg: "#f5f0ff", border: "#7C3AED", angle: 200, radius: 240 },
  { text: "AI-Powered", icon: "ri-cpu-line", color: "#6366f1", bg: "#eef2ff", border: "#6366f1", angle: 120, radius: 240 },
  { text: "Emotionally Aware", icon: "ri-heart-pulse-line", color: "#e11d48", bg: "#fff1f2", border: "#e11d48", angle: 240, radius: 240 },
  { text: "Always Listening", icon: "ri-headphone-line", color: "#0891b2", bg: "#f0f9ff", border: "#0891b2", angle: 90, radius: 240 },
];

function CenterBotAnimation() {
  const [orbitAngle, setOrbitAngle] = useState(0);
  const [videoOpacity, setVideoOpacity] = useState(1);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let running = true;
    function tick(time: number) {
      if (!running) return;
      if (lastTimeRef.current) {
        const delta = time - lastTimeRef.current;
        setOrbitAngle((a) => (a + delta * 0.04) % 360);
      }
      lastTimeRef.current = time;
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleCanPlay = () => { video.playbackRate = 1.25; };
    video.addEventListener("canplay", handleCanPlay);
    if (video.readyState >= 3) video.playbackRate = 1.25;
    return () => video.removeEventListener("canplay", handleCanPlay);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let fading = false;
    const handleTimeUpdate = () => {
      if (!video.duration) return;
      const remaining = video.duration - video.currentTime;
      if (remaining <= 0.5 && !fading) {
        fading = true;
        setVideoOpacity(0);
        setTimeout(() => { setVideoOpacity(1); fading = false; }, 350);
      }
    };
    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, []);

  return (
    <div className="relative flex items-center justify-center" style={{ width: 560, height: 560 }}>
      <style>{`
        @keyframes heroPulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.4); opacity: 1; }
        }
        @keyframes glowBreath {
          0%, 100% { opacity: 0.18; transform: scale(0.95); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }
        @keyframes orbitSpin1 {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes orbitSpin2 {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(-360deg); }
        }
      `}</style>

      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 420, height: 420, top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(192,132,252,0.55) 0%, rgba(124,58,237,0.3) 45%, transparent 72%)",
          animation: "glowBreath 3s ease-in-out infinite",
        }}
      />
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 490, height: 490, top: "50%", left: "50%",
          border: "1.5px dashed rgba(168,85,247,0.35)",
          animation: "orbitSpin1 28s linear infinite",
        }}
      />
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 440, height: 440, top: "50%", left: "50%",
          border: "1px solid rgba(124,58,237,0.2)",
          animation: "orbitSpin2 18s linear infinite",
        }}
      >
        <div className="absolute rounded-full" style={{ width: 7, height: 7, background: "#A855F7", top: -3.5, left: "50%", transform: "translateX(-50%)", boxShadow: "0 0 8px rgba(168,85,247,0.8)" }} />
        <div className="absolute rounded-full" style={{ width: 5, height: 5, background: "#7C3AED", bottom: -2.5, left: "50%", transform: "translateX(-50%)", boxShadow: "0 0 6px rgba(124,58,237,0.7)" }} />
      </div>
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 395, height: 395, top: "50%", left: "50%",
          border: "1px dotted rgba(168,85,247,0.18)",
          animation: "orbitSpin1 40s linear infinite",
        }}
      />

      {orbitLabels.map((label, i) => {
        const rad = ((label.angle + orbitAngle) * Math.PI) / 180;
        const x = Math.cos(rad) * label.radius;
        const y = Math.sin(rad) * label.radius * 0.55;
        return (
          <div
            key={i}
            className="absolute flex items-center gap-1.5 rounded-full px-3 py-1.5 border text-xs font-semibold whitespace-nowrap"
            style={{
              background: label.bg, borderColor: label.border + "60", color: label.color,
              left: "50%", top: "50%",
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
              zIndex: 20, boxShadow: `0 2px 12px ${label.color}22`,
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${label.icon} text-xs`}></i>
            </div>
            {label.text}
          </div>
        );
      })}

      <div
        className="relative z-10 flex items-center justify-center"
        style={{
          width: 420, height: 420, borderRadius: "50%", overflow: "hidden",
          WebkitMaskImage: "radial-gradient(ellipse 80% 85% at 50% 52%, black 38%, rgba(0,0,0,0.92) 52%, rgba(0,0,0,0.6) 65%, rgba(0,0,0,0.15) 80%, transparent 100%)",
          maskImage: "radial-gradient(ellipse 80% 85% at 50% 52%, black 38%, rgba(0,0,0,0.92) 52%, rgba(0,0,0,0.6) 65%, rgba(0,0,0,0.15) 80%, transparent 100%)",
        }}
      >
        <video
          ref={videoRef}
          src={SITE_MEDIA.avaHeroVideo}
          autoPlay
          loop
          muted
          playsInline
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            mixBlendMode: "multiply", opacity: videoOpacity,
            transition: videoOpacity === 0 ? "opacity 0.25s ease-out" : "opacity 0.25s ease-in",
          }}
        />
      </div>

      <div
        className="absolute bottom-16 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full z-10"
        style={{ background: "#A855F7", animation: "heroPulse 1.8s ease-in-out infinite" }}
      />
    </div>
  );
}


function scrollToHeroAndRipple() {
  window.scrollTo({ top: 0, behavior: "smooth" });
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("ava-ripple-stores"));
  }, 800);
}

export default function Hero() {
  const [mounted, setMounted] = useState(false);
  const [rippleGoogle, setRippleGoogle] = useState(false);
  const [rippleApple, setRippleApple] = useState(false);
  const [rippleCTA, setRippleCTA] = useState(false);
  const [storesRippling, setStoresRippling] = useState(false);
  const ctaBuzzTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctaBuzzIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = () => {
      setStoresRippling(true);
      setRippleGoogle(true);
      setRippleApple(false);
      setRippleCTA(false); // pause CTA buzz
      const t1 = setTimeout(() => {
        setRippleGoogle(false);
        setRippleApple(true);
      }, 2500);
      const t2 = setTimeout(() => {
        setRippleApple(false);
        setRippleGoogle(true);
        const t3 = setTimeout(() => {
          setRippleGoogle(false);
          setRippleApple(true);
          const t4 = setTimeout(() => {
            setRippleApple(false);
            setStoresRippling(false);
            // resume CTA buzz
            setRippleCTA(true);
            const t5 = setTimeout(() => setRippleCTA(false), 2000);
            return () => clearTimeout(t5);
          }, 2500);
          return () => clearTimeout(t4);
        }, 2500);
        return () => clearTimeout(t3);
      }, 5000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    };
    window.addEventListener("ava-ripple-stores", handler);
    return () => window.removeEventListener("ava-ripple-stores", handler);
  }, []);

  // Buzz the CTA button periodically to draw attention
  useEffect(() => {
    const startBuzz = () => {
      if (storesRippling) return;
      setRippleCTA(true);
      const t = setTimeout(() => setRippleCTA(false), 2000);
      return t;
    };
    // Initial buzz after 3s
    const t1 = setTimeout(() => {
      startBuzz();
      // Then every 6s
      const interval = setInterval(() => {
        startBuzz();
      }, 6000);
      ctaBuzzIntervalRef.current = interval;
      return () => clearInterval(interval);
    }, 3000);
    ctaBuzzTimerRef.current = t1;
    return () => {
      clearTimeout(t1);
      if (ctaBuzzIntervalRef.current) clearInterval(ctaBuzzIntervalRef.current);
    };
  }, [storesRippling]);

  return (
    <section
      id="hero"
      className="relative min-h-[100vh] lg:h-[100vh] lg:min-h-[720px] flex items-center overflow-visible lg:overflow-hidden"
      style={{ position: "relative" }}
    >
      {/* Animated shader background */}
      <HeroShaderBackground />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full opacity-10"
          viewBox="0 0 1400 500"
          fill="none"
        >
          <path d="M0 250 Q350 100 700 250 Q1050 400 1400 250" stroke="#7C3AED" strokeWidth="1.5" fill="none" />
          <path d="M0 270 Q350 120 700 270 Q1050 420 1400 270" stroke="#A855F7" strokeWidth="1" fill="none" />
          <path d="M0 230 Q350 80 700 230 Q1050 380 1400 230" stroke="#7C3AED" strokeWidth="0.8" fill="none" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full pt-20 md:pt-24 pb-10 md:pb-12">
        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-2">

          {/* Left — Text + CTA */}
          <div
            className="flex-1 lg:flex-[1.4] text-center lg:text-left lg:pr-4 transition-all duration-700 w-full max-w-[700px] mx-auto lg:mx-0"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateX(0)" : "translateX(-40px)" }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#7C3AED]/20 text-[#7C3AED] text-sm font-medium mb-5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span>
              Your AI Companion — Always Here
            </div>

            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a1040] leading-tight mb-2">
              You show up for everyone…
              <br />
              but nobody really shows up for you.
            </h1>

            {/* Typewriter subheadline */}
            <h2 className="font-display text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold leading-tight mb-5 min-h-[2.5rem] md:min-h-[3rem]">
              <TypewriterSubheadline />
            </h2>

            {/* Single CTA: title + subtitle inside button */}
            <div className="flex flex-col items-center lg:items-start gap-2 mb-6">
              <button
                type="button"
                onClick={() => openLiveDemoChat()}
                className={`whitespace-nowrap flex flex-col items-center justify-center gap-0.5 px-8 py-3.5 rounded-full text-white font-bold cursor-pointer hover:opacity-90 transition-all hover:scale-105 ${rippleCTA ? "cta-buzz" : ""}`}
                style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
              >
                <span className="flex items-center gap-2 text-base font-bold">
                  <i className="ri-chat-smile-3-line text-xl w-5 h-5 flex items-center justify-center"></i>
                  Talk to Ava now
                </span>
                <span className="text-white/75 text-xs font-normal">Try your first conversation (free)</span>
              </button>
            </div>

            <p className="text-[#9a8aaa] text-sm italic mb-5 text-center lg:text-left">
              Private. Secure. Built by Neuroava.
            </p>

            {/* App Store Buttons */}
            <style>{`
              @keyframes ctaBuzz {
                0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(124,58,237,0.5); }
                20% { transform: scale(1.06); box-shadow: 0 0 0 10px rgba(124,58,237,0.2); }
                40% { transform: scale(1.03); box-shadow: 0 0 0 18px rgba(124,58,237,0); }
                60% { transform: scale(1.05); box-shadow: 0 0 0 0 rgba(124,58,237,0); }
                80% { transform: scale(1.02); }
              }
              .cta-buzz {
                animation: ctaBuzz 0.6s ease-in-out 3;
              }
              @keyframes storeRipple {
                0% { box-shadow: 0 0 0 0 rgba(124,58,237,0.55); }
                70% { box-shadow: 0 0 0 16px rgba(124,58,237,0); }
                100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); }
              }
              @keyframes storeBounce {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.07); }
              }
              .store-ripple {
                animation: storeRipple 1s ease-out infinite, storeBounce 0.7s ease-in-out infinite;
              }
            `}</style>
            <div className="flex flex-wrap items-center gap-3 justify-center lg:justify-start mb-4 w-full" id="hero-store-buttons">
              <a
                href="https://play.google.com/store/apps/details?id=com.avachat.ai.app&hl=en"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={`whitespace-nowrap flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-[#1a1040] text-white cursor-pointer hover:opacity-90 transition-all hover:scale-105 ${rippleGoogle ? "store-ripple" : ""}`}
              >
                <i className="ri-google-play-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                <div className="text-left">
                  <p className="text-[10px] text-white/60 leading-none">Get it on</p>
                  <p className="font-bold text-sm leading-tight">Google Play</p>
                </div>
              </a>
              <a
                href="https://apps.apple.com/us/app/avachatai/id6745537736"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={`whitespace-nowrap flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-[#1a1040] text-white cursor-pointer hover:opacity-90 transition-all hover:scale-105 ${rippleApple ? "store-ripple" : ""}`}
              >
                <i className="ri-apple-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                <div className="text-left">
                  <p className="text-[10px] text-white/60 leading-none">Download on the</p>
                  <p className="font-bold text-sm leading-tight">App Store</p>
                </div>
              </a>
            </div>

            <div className="flex items-center gap-2 justify-center lg:justify-start text-[#7a6aaa] text-sm">
              <i className="ri-gift-line text-[#7C3AED] w-4 h-4 flex items-center justify-center"></i>
              <span>10 free chats to start — no credit card needed</span>
            </div>
          </div>

          {/* Center — Bot animation (desktop only) */}
          <div
            className="flex-shrink-0 hidden lg:flex items-center justify-center transition-all duration-700 delay-300"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "scale(1)" : "scale(0.8)" }}
          >
            <CenterBotAnimation />
          </div>

          {/* Right — Phone Mockup */}
          <div
            className="w-full flex justify-center lg:flex-1 lg:justify-end lg:pl-4 transition-all duration-700 delay-500"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateX(0)" : "translateX(40px)" }}
          >
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}